import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PlaceInput from "./PlaceInput";
import { geocodeAddress, RADIUS_OPTIONS } from "./places";

// "2026-10-20T08:00" <-> ISO-String (für <input type="datetime-local">)
const toLocalInput = (iso, withTime = true) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return withTime ? `${date}T${pad(d.getHours())}:${pad(d.getMinutes())}` : date;
};

const parsePlace = (raw) => {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Suchformular für Start- und Ergebnisseite.
 * initial: Werte aus der URL · compact: einzeilige Leiste (Ergebnisseite) · onSearch(params): fertige Such-Parameter
 */
const SearchForm = ({ initial = {}, compact = false, onSearch, submitLabel }) => {
  const { t } = useTranslation();
  const initialPlace = parsePlace(initial.parkingAt);
  const [tab, setTab] = useState(["parking-monthly", "parking-night"].includes(initial.activeTab) ? initial.activeTab : "parking-hourly");
  const [placeText, setPlaceText] = useState(initialPlace ? initialPlace.location : "");
  const [place, setPlace] = useState(initialPlace);
  const [from, setFrom] = useState(toLocalInput(initial.parkingFrom, tab === "parking-hourly"));
  const [until, setUntil] = useState(toLocalInput(initial.parkingUntil));
  const [start, setStart] = useState(toLocalInput(initial.parkingFrom, false));
  const [duration, setDuration] = useState(initial.duration || "Seven Days Per Week");
  const [radius, setRadius] = useState(Number(initial.radius) || 2);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const isHourly = tab === "parking-hourly";
  const isNight = tab === "parking-night";

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (placeText.trim().length < 2) next.place = t("rpErrPlace");
    if (isHourly) {
      if (!from) next.from = t("rpErrFrom");
      if (!until || (from && until <= from)) next.until = t("rpErrUntil");
    } else if (!start) {
      next.start = t(isNight ? "rxErrNight" : "rpErrStart");
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    // Vorschlag gewählt -> Koordinaten liegen vor; sonst Freitext (Adresse) einmalig auflösen
    let target = place && place.location === placeText ? place : null;
    if (!target) {
      setBusy(true);
      try {
        target = await geocodeAddress(placeText.trim());
      } catch (err) {
        target = null;
      }
      setBusy(false);
      if (!target) {
        setErrors({ place: t("rpErrPlaceNotFound") });
        return;
      }
      setPlace(target);
    }

    const params = {
      parkingAt: JSON.stringify({ lat: target.lat, lng: target.lng, location: placeText.trim() }),
      activeTab: tab,
      radius: String(radius),
    };
    if (isHourly) {
      params.parkingFrom = new Date(from).toISOString();
      params.parkingUntil = new Date(until).toISOString();
    } else if (isNight) {
      // Nachtparken: 18:00 bis 7:00 am Folgetag (genaue Zeiten liefert der Tarif des Standorts)
      const night = new Date(`${start}T18:00`);
      const morning = new Date(`${start}T07:00`);
      morning.setDate(morning.getDate() + 1);
      params.parkingFrom = night.toISOString();
      params.parkingUntil = morning.toISOString();
    } else {
      params.parkingFrom = new Date(start).toISOString();
      params.duration = duration;
    }
    onSearch(params);
  };

  const field = (key, label, input) => (
    <div className={`ipl-field ${errors[key] ? "has-error" : ""}`}>
      <label>{label}</label>
      {input}
      <div className="ipl-err">{errors[key]}</div>
    </div>
  );

  const placeField = (
    <div className={`ipl-field ${errors.place ? "has-error" : ""}`}>
      <label htmlFor="rp-place">{t("parkingAt")}</label>
      <div className="ipl-plate-row">
        <PlaceInput
          id="rp-place"
          value={placeText}
          placeholder={t("rpPlacePlaceholder")}
          onTextChange={(v) => {
            setPlaceText(v);
            setErrors((x) => ({ ...x, place: undefined }));
          }}
          onSelect={(p) => {
            setPlace(p);
            setPlaceText(p.location);
            setErrors((x) => ({ ...x, place: undefined }));
          }}
        />
        <div className="ipl-country rp-radius">
          <select aria-label={t("rpRadius")} value={radius} onChange={(e) => setRadius(Number(e.target.value))}>
            {RADIUS_OPTIONS.map((r) => (
              <option key={r} value={r}>{`${r} km`}</option>
            ))}
          </select>
        </div>
      </div>
      {!compact && <div className="ipl-hint">{t("rpPlaceHint")}</div>}
      <div className="ipl-err">{errors.place}</div>
    </div>
  );

  const timeFields = isHourly ? (
    <>
      {field("from", t("parkingFrom"), <div className="ipl-input"><input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} /></div>)}
      {field("until", t("parkingUntil"), <div className="ipl-input"><input type="datetime-local" value={until} min={from || undefined} onChange={(e) => setUntil(e.target.value)} /></div>)}
    </>
  ) : isNight ? (
    <>
      {field("start", t("rxNightOf"), <div className="ipl-input"><input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>)}
      <div className="ipl-field rx-night-hint">
        <label>&nbsp;</label>
        <div className="ipl-hint">{t("rxNightHint")}</div>
      </div>
    </>
  ) : (
    <>
      {field("start", t("rpParkingStart"), <div className="ipl-input"><input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>)}
      {field(
        "duration",
        t("duration"),
        <div className="ipl-input">
          <select value={duration} onChange={(e) => setDuration(e.target.value)}>
            <option value="Seven Days Per Week">{t("rpMonSun")}</option>
            <option value="Mon-Fri">{t("rpMonFri")}</option>
          </select>
        </div>
      )}
    </>
  );

  const tabs = (
    <div className="ipl-seg" role="group" aria-label={t("rpTariff")}>
      <button type="button" aria-pressed={isHourly} onClick={() => setTab("parking-hourly")}>
        {t("rxTabHour")}
      </button>
      <button type="button" aria-pressed={tab === "parking-monthly"} onClick={() => setTab("parking-monthly")}>
        {t("rxTabMonth")}
      </button>
      <button type="button" aria-pressed={isNight} onClick={() => setTab("parking-night")}>
        {t("rxTabNight")}
      </button>
    </div>
  );

  const button = (
    <button className={`ipl-btn ${compact ? "rp-search-btn" : ""} ${busy ? "loading" : ""}`} type="submit" disabled={busy}>
      <span className="ipl-spinner" aria-hidden="true"></span>
      <span>{submitLabel || t("showParking")}</span>
    </button>
  );

  if (compact) {
    return (
      <form className="rp-search-wrap" onSubmit={submit} noValidate>
        {tabs}
        <div className="rp-search">
          {placeField}
          {timeFields}
          {button}
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      {tabs}
      {placeField}
      <div className="ipl-two">{timeFields}</div>
      {button}
    </form>
  );
};

export default SearchForm;
